import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, Lock, Mail } from "lucide-react";
import { TintiLogo } from "@/components/layout/TintiLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";

export default function Login() {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    const to = location.state?.from?.pathname || "/";
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await login(email, password);
    setSubmitting(false);
    if (!result.ok) setError(result.error);
  };

  return (
    <div className="tc-grid-bg flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="mb-8 flex justify-center"><TintiLogo className="h-12 sm:h-14" /></div>
        <Card className="border-line bg-surface">
          <CardContent className="p-6">
            <h1 className="mb-1 font-heading text-xl font-bold text-foreground">Entrar</h1>
            <p className="mb-6 text-sm text-muted-foreground">Acesse o Control Plane da TintiCloud.</p>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="login-email">E-mail</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="login-email" type="email" required autoFocus autoComplete="username"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="border-line bg-background pl-9" data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password">Senha</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="login-password" type="password" required autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="border-line bg-background pl-9" data-testid="login-password-input"
                  />
                </div>
              </div>
              {error && (
                <p className="rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err" data-testid="login-error">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={submitting} className="w-full bg-brand text-white hover:bg-brand-hover" data-testid="login-submit-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Entrar
              </Button>
            </form>
            <a href="/forgot-password" className="text-sm text-brand">Esqueci minha senha</a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
